/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { FiCopy, FiExternalLink, FiLayout } from "react-icons/fi";
import { useUI } from "../theme/ThemeProvider";
import WhatsLayout from "../layouts/WhatsLayout";
import Sales from "../pages/AdminDashboard/SalesPage";
import CustomersSummary from "../pages/AdminDashboard/CustomersSummaryPage";
import RecentOrders from "../pages/AdminDashboard/RecentOrdersPage";
import { useNavigate } from "react-router-dom";
import {
  SUBSCRIPTION_STATUS_EVENT,
  isSubscriptionMarkedExpired,
  navigateWithExpiredPlanGuard,
} from "../utils/expiredPlanAccess";

export default function Dashboard() {
  const { theme, lang } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const navigate = useNavigate();
  const [storeUrl, setStoreUrl] = React.useState("");
  const [linkCopied, setLinkCopied] = React.useState(false);
  const [planExpired, setPlanExpired] = React.useState(isSubscriptionMarkedExpired);

  const storeCardStyle = {
    background: isDark
      ? "linear-gradient(145deg, #1b1030 0%, #2a1650 55%, #1a0f2e 100%)"
      : "linear-gradient(145deg, #fbf7ff 0%, #f3e8ff 48%, #ffffff 100%)",
    border: isDark ? "1px solid rgba(168, 85, 247, 0.28)" : "1px solid rgba(116, 0, 191, 0.16)",
    boxShadow: isDark
      ? "0 10px 28px rgba(116, 0, 191, 0.22)"
      : "0 10px 28px rgba(116, 0, 191, 0.08)",
    cursor: "pointer",
    textAlign: "start",
    position: "relative",
    overflow: "hidden",
  };

  const themesCardStyle = {
    background: isDark
      ? "linear-gradient(145deg, #2a210f 0%, #3d2e12 55%, #241c0c 100%)"
      : "linear-gradient(145deg, #fffbeb 0%, #fef3c7 48%, #ffffff 100%)",
    border: isDark ? "1px solid rgba(242, 180, 65, 0.32)" : "1px solid rgba(242, 180, 65, 0.34)",
    boxShadow: isDark
      ? "0 10px 28px rgba(242, 180, 65, 0.12)"
      : "0 10px 28px rgba(242, 180, 65, 0.12)",
    cursor: "pointer",
    textAlign: "start",
    position: "relative",
    overflow: "hidden",
  };

  useEffect(() => {
    const syncPlan = () => setPlanExpired(isSubscriptionMarkedExpired());
    window.addEventListener(SUBSCRIPTION_STATUS_EVENT, syncPlan);
    return () => window.removeEventListener(SUBSCRIPTION_STATUS_EVENT, syncPlan);
  }, []);

  useEffect(() => {
    try {
      const tenant = JSON.parse(localStorage.getItem("tenant") || "{}");
      const slug = tenant.storeSlug || tenant.StoreSlug || localStorage.getItem("companyStoreSlug");
      if (slug) {
        setStoreUrl(`https://${slug}.madar.iq/`);
      }
    } catch (e) {
      console.error("Error parsing tenant data", e);
    }
  }, []);

  return (
    <WhatsLayout title={null}>
      <style>{`
        .dash-explore-card {
          transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
                      box-shadow 0.35s ease,
                      background 0.35s ease,
                      border-color 0.35s ease;
        }
        .dash-explore-card::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.42) 45%, transparent 70%);
          transform: translateX(-120%);
          transition: transform 0.7s ease;
          pointer-events: none;
        }
        .dash-explore-card::after {
          content: "";
          position: absolute;
          width: 92px;
          height: 92px;
          border-radius: 50%;
          top: -30px;
          inset-inline-end: -22px;
          pointer-events: none;
        }
        .dash-explore-store::after {
          background: radial-gradient(circle, rgba(116, 0, 191, 0.16) 0%, transparent 70%);
        }
        .dash-explore-themes::after {
          background: radial-gradient(circle, rgba(242, 180, 65, 0.28) 0%, transparent 70%);
        }
        .dash-explore-card:hover::before {
          transform: translateX(120%);
        }
        .dash-explore-card:hover {
          transform: translateY(-4px);
        }
        .dash-explore-store:hover {
          border-color: #7400BF !important;
          box-shadow: 0 16px 36px rgba(116, 0, 191, 0.2);
        }
        .dash-explore-themes:hover {
          border-color: #F2B441 !important;
          box-shadow: 0 16px 36px rgba(242, 180, 65, 0.26);
        }
        .dash-explore-icon {
          transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .dash-explore-store:hover .dash-explore-icon {
          transform: translateX(-3px) translateY(-2px) scale(1.08);
        }
        .dash-explore-themes:hover .dash-explore-icon {
          transform: rotate(-10deg) scale(1.12);
        }
        .dash-explore-chip {
          transition: background 0.25s ease, transform 0.25s ease;
        }
        .dash-explore-store:hover .dash-explore-chip {
          background: rgba(116, 0, 191, 0.16) !important;
        }
        .dash-explore-themes:hover .dash-explore-chip {
          background: rgba(242, 180, 65, 0.38) !important;
        }
        .dash-explore-card.is-dark::before {
          background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.1) 45%, transparent 70%);
        }
        .dash-explore-store.is-dark:hover .dash-explore-chip {
          background: rgba(116, 0, 191, 0.45) !important;
        }
        .dash-explore-themes.is-dark:hover .dash-explore-chip {
          background: rgba(242, 180, 65, 0.28) !important;
        }
        .dash-copy-link-btn {
          transition: transform 0.2s ease, background 0.2s ease, opacity 0.2s ease;
        }
        .dash-copy-link-btn:hover {
          transform: translateY(-1px);
          opacity: 0.95;
        }
        .dash-copy-link-btn:active {
          transform: scale(0.97);
        }
        .dash-copy-label-short {
          display: none;
        }
        @media (max-width: 767px) {
          .dash-explore-cards {
            flex-direction: column;
            width: 100%;
            max-width: none;
          }
          .dash-explore-card {
            width: 100%;
            flex: 0 0 auto;
          }
          .dash-copy-label-full {
            display: none;
          }
          .dash-copy-label-short {
            display: inline;
          }
        }
        @media (min-width: 768px) {
          .dash-copy-label-short {
            display: none;
          }
        }
      `}</style>
      <div
        dir={isArabic ? "rtl" : "ltr"}
        className={`w-full min-h-screen transition-colors duration-300 ${isDark ? "bg-[#060b15]" : "bg-gray-50"}`}
        style={{ paddingBottom: "2rem" }}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 space-y-8 sm:space-y-10">

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-2">
            <h2 className={`shrink-0 text-xl sm:text-2xl font-extrabold tracking-tight ${isDark ? "text-[#F2B441]" : "text-[#21003D]"}`}>
              {isArabic ? "لوحة التحكم" : "Dashboard"}
            </h2>

            <div dir="ltr" className="dash-explore-cards flex flex-col md:flex-row items-stretch gap-2.5 w-full lg:w-[68%] lg:max-w-[720px]">
              {storeUrl && (
                <motion.div
                  role="link"
                  tabIndex={0}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                  onClick={() => window.open(storeUrl, "_blank", "noopener,noreferrer")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") window.open(storeUrl, "_blank", "noopener,noreferrer");
                  }}
                  className={`dash-explore-card dash-explore-store w-full min-w-0 md:flex-[1.2_1_0%] rounded-2xl px-3 sm:px-3.5 py-3 flex items-center ${isDark ? "is-dark" : ""}`}
                  style={storeCardStyle}
                >
                  <div dir={isArabic ? "rtl" : "ltr"} className="relative z-[1] flex items-center gap-2 sm:gap-3 min-w-0 w-full">
                    <div
                      className="dash-explore-icon shrink-0 flex items-center justify-center rounded-xl"
                      style={{
                        width: 38,
                        height: 38,
                        background: isDark ? "rgba(116, 0, 191, 0.35)" : "rgba(116, 0, 191, 0.12)",
                        color: isDark ? "#E9D5FF" : "#7400BF",
                      }}
                    >
                      <FiExternalLink size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className="truncate"
                        style={{
                          margin: 0,
                          marginBottom: 5,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          letterSpacing: "0.01em",
                          color: isDark ? "rgba(233, 213, 255, 0.72)" : "#7c3aed",
                        }}
                      >
                        {isArabic ? "متجرك الإلكتروني" : "Your online store"}
                      </p>
                      <span
                        className="dash-explore-chip flex items-center gap-1.5 min-w-0 max-w-full rounded-lg px-2 py-0.5"
                        style={{
                          background: isDark ? "rgba(116, 0, 191, 0.28)" : "rgba(116, 0, 191, 0.1)",
                          color: isDark ? "#F3E8FF" : "#21003D",
                          fontWeight: 800,
                          fontSize: "0.82rem",
                        }}
                      >
                        <span className="truncate" dir="ltr">
                          {storeUrl.replace("https://", "").replace(/\/$/, "")}
                        </span>
                        <FiExternalLink size={12} className="shrink-0" />
                      </span>
                    </div>
                    <button
                      type="button"
                      className="dash-copy-link-btn relative z-[2] shrink-0 inline-flex items-center gap-1 sm:gap-1.5 rounded-lg px-2 sm:px-2.5 py-1.5"
                      style={{
                        background: isDark ? "rgba(116, 0, 191, 0.55)" : "#7400BF",
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: "0.72rem",
                        border: "none",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        navigator.clipboard.writeText(storeUrl).then(() => {
                          setLinkCopied(true);
                          window.setTimeout(() => setLinkCopied(false), 1500);
                        });
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <FiCopy size={12} className="shrink-0" />
                      {linkCopied ? (
                        <>
                          <span className="dash-copy-label-short">{isArabic ? "تم" : "Copied"}</span>
                          <span className="dash-copy-label-full">{isArabic ? "تم النسخ" : "Copied"}</span>
                        </>
                      ) : (
                        <>
                          <span className="dash-copy-label-short">{isArabic ? "نسخ" : "Copy"}</span>
                          <span className="dash-copy-label-full">{isArabic ? "نسخ الرابط" : "Copy link"}</span>
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}

              <motion.div
                role="button"
                tabIndex={0}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.08, ease: "easeOut" }}
                onClick={() =>
                  navigateWithExpiredPlanGuard(navigate, "/admin-store/store/theme", {
                    isArabic,
                    isDark,
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    navigateWithExpiredPlanGuard(navigate, "/admin-store/store/theme", {
                      isArabic,
                      isDark,
                    });
                  }
                }}
                className={`dash-explore-card dash-explore-themes w-full min-w-0 md:flex-[1_1_0%] rounded-2xl px-3 sm:px-3.5 py-3 flex items-center ${isDark ? "is-dark" : ""}`}
                style={themesCardStyle}
              >
                <div dir={isArabic ? "rtl" : "ltr"} className="relative z-[1] flex items-center gap-2 sm:gap-3 min-w-0 w-full">
                  <div
                    className="dash-explore-icon shrink-0 flex items-center justify-center rounded-xl"
                    style={{
                      width: 38,
                      height: 38,
                      background: isDark ? "rgba(242, 180, 65, 0.22)" : "rgba(242, 180, 65, 0.28)",
                      color: isDark ? "#F2B441" : "#92400e",
                    }}
                  >
                    <FiLayout size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className="truncate"
                      style={{
                        margin: 0,
                        marginBottom: 5,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.01em",
                        color: isDark ? "rgba(253, 230, 138, 0.78)" : "#b45309",
                      }}
                    >
                      {isArabic ? "الثيمات المتاحة" : "Available themes"}
                    </p>
                    <span
                      className="dash-explore-chip flex items-center min-w-0 max-w-full rounded-lg px-2 py-0.5"
                      style={{
                        background: isDark ? "rgba(242, 180, 65, 0.16)" : "rgba(242, 180, 65, 0.22)",
                        color: isDark ? "#FEF3C7" : "#21003D",
                        fontWeight: 800,
                        fontSize: "0.82rem",
                      }}
                    >
                      <span className="truncate">
                        {isArabic ? "تصفح التصاميم الجاهزة" : "Browse ready designs"}
                      </span>
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {planExpired ? (
            <div
              className={`rounded-2xl border px-5 py-8 text-center ${
                isDark
                  ? "border-red-500/30 bg-red-500/10 text-red-200"
                  : "border-red-200 bg-red-50 text-red-700"
              }`}
            >
              <p className="text-base sm:text-lg font-extrabold">
                {isArabic ? "الخطة منتهية" : "Plan expired"}
              </p>
              <p className="mt-2 text-sm font-medium opacity-80">
                {isArabic
                  ? "بيانات لوحة التحكم غير متاحة حتى تجديد الاشتراك."
                  : "Dashboard data is unavailable until the subscription is renewed."}
              </p>
            </div>
          ) : (
            <>
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="w-full"
              >
                <Sales />
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="w-full"
              >
                <CustomersSummary />
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className="w-full"
              >
                <RecentOrders />
              </motion.section>
            </>
          )}
        </div>
      </div>
    </WhatsLayout>
  );
}
